/* eslint-disable @typescript-eslint/no-explicit-any */
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import LoadingContainer from "@componentsV1/containers/LoadingContainer";
import LegalData from "@componentsV1/pages/Payment/molecules/LegalData";
import PriceType from "@componentsV1/pages/Payment/molecules/PriceType";
import RightSideBlock from "@componentsV1/pages/Payment/molecules/RightSideBlock";
import Button from "@componentsV1/shared/Button";
import { useLazyCreatePaymentIntentQuery } from "@store/services/PaymentService";
import { useLazyConfirmReservationQuery } from "@store/services/ReservationService";
import { showToast } from "@store/slices/toastSlice";
import { useTypedDispatch, useTypedSelector } from "@store/store";
import TOAST from "@utils/constants/toast";
import { prepareParams } from "@utils/helpers/rooms";

import { usePaymentState } from "./hooks/usePaymentState";
import styles from "./index.module.scss";

interface TransactionType {
  full?: number;
  deposit?: number;
  amount: number;
  currency: string;
  transaction_type: string;
}

function StripePaymentForm({ transaction }: { transaction: TransactionType }) {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const [createIntent] = useLazyCreatePaymentIntentQuery();
  const [confirmReservation] = useLazyConfirmReservationQuery();
  const [searchParams] = useSearchParams();

  const { cruiseId } = useParams();

  const { rooms } = useTypedSelector((state) => state.rooms);
  const source = searchParams.get("source") ?? "";

  const {
    terms_and_conditions,
    liability_and_cancellation,
    participant_release_agreement,
    supplemental_terms,
    show_cancellation_policy,
    guest_ticket_contract,
  } = useTypedSelector((state) => state.environment);

  const stripe = useStripe();
  const elements = useElements();

  const { isLoading, setIsLoading, errorMessage, setErrorMessage } = usePaymentState();

  const [legalData, setLegalData] = useState({
    terms_and_conditions: !terms_and_conditions,
    participant_release_agreement: !participant_release_agreement,
    supplemental_terms: !supplemental_terms,
    guest_ticket_contract: !guest_ticket_contract,
    show_cancellation_policy: !show_cancellation_policy,
    liability_and_cancellation: !liability_and_cancellation,
  });

  const params = useMemo(() => {
    const totalRooms = +(searchParams.get("rooms") ?? 0);
    const currentRoomNumber = +(searchParams.get("room") ?? 0);

    const output = prepareParams(
      searchParams,
      rooms ?? {},
      currentRoomNumber,
      currentRoomNumber < totalRooms,
    );

    return output;
  }, [searchParams, rooms]);

  const handleLegalDataChange = ({ value, valueKey }: { value: boolean; valueKey: string }) => {
    setLegalData((prev) => ({ ...prev, [valueKey]: value }));
  };

  const onPaymentFail = () => {
    dispatch(
      showToast({
        type: TOAST.ERROR_TYPE,
        message: t("Payment failed"),
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const onPaymentSuccess = () => {
    dispatch(
      showToast({
        type: TOAST.SUCCESS_TYPE,
        message: t("Payment success"),
        duration: TOAST.DEFAULT_DURATION,
      }),
    );

    navigate(`/search-results/${cruiseId}/payment-confirmation?${params}`);
  };

  const handleConfirm = async () => {
    if (!stripe || !elements) {
      dispatch(
        showToast({
          type: TOAST.ERROR_TYPE,
          message: t("Stripe not initialized"),
          duration: TOAST.DEFAULT_DURATION,
        }),
      );

      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const { error: submitError } = await elements.submit();

      if (submitError) {
        throw new Error(submitError.message);
      }

      const { amount, currency } = transaction;

      const { data } = await createIntent({
        pnr: rooms?.[1]?.pnr ?? "",
        transactions: [{ amount, currency }],
        source,
      });

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: data?.client_secret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        throw new Error(error.message || "Stripe payment failed");
      }

      const intentTransactions = [
        {
          transaction_type: "creditcard" as const,
          made_on: new Date(paymentIntent.created * 1000).toISOString(),
          made_by: "Website" as const,
          amount: paymentIntent.amount,
          currency: paymentIntent.currency,
          card_clearance: {
            clearance_system: "stripe" as const,
            auth_code: paymentIntent.id,
            transaction_id: paymentIntent.id,
            other: {
              card_type: (paymentIntent.payment_method as any)?.card?.brand || "unknown",
              last_four: (paymentIntent.payment_method as any)?.card?.last4 || "0000",
              transaction_id: paymentIntent.id,
              intent_id: paymentIntent.id,
            },
          },
        },
      ];

      await confirmReservation({
        pnr: rooms?.[1]?.pnr ?? "",
        transactions: intentTransactions,
      });

      setIsLoading(false);
      onPaymentSuccess();
    } catch (error) {
      const errorText = error instanceof Error ? error.message : JSON.stringify(error);
      setIsLoading(false);
      onPaymentFail();
      setErrorMessage(errorText);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const legalDataAccepted =
    legalData.guest_ticket_contract &&
    legalData.participant_release_agreement &&
    legalData.show_cancellation_policy &&
    legalData.supplemental_terms &&
    legalData.terms_and_conditions &&
    legalData.liability_and_cancellation;

  return (
    <>
      {errorMessage.length > 0 && <div className={styles.inputError}>{errorMessage}</div>}

      <div className={styles.fieldContainer}>
        <PaymentElement />
      </div>

      <LegalData
        terms_and_conditions_checked={legalData.terms_and_conditions}
        participant_release_agreement_checked={legalData.participant_release_agreement}
        supplemental_terms_checked={legalData.supplemental_terms}
        guest_ticket_contract_checked={legalData.guest_ticket_contract}
        show_cancellation_policy_checked={legalData.show_cancellation_policy}
        liability_and_cancellation_checked={legalData.liability_and_cancellation}
        onChange={handleLegalDataChange}
      />

      <div className={styles.inputs}>
        <Button
          label={isLoading ? t("Loading") : t("Pay")}
          className={styles.button}
          onClick={handleConfirm}
          loading={isLoading}
          disabled={!stripe || !elements || isLoading || !legalDataAccepted}
        />
      </div>
    </>
  );
}

function Payment() {
  const { rooms } = useTypedSelector((state) => state.rooms);
  const { stripe_publishable_key } = useTypedSelector((state) => state.environment);
  const { isCruiseLoading } = useTypedSelector((state) => state.search);
  const { t } = useTranslation();

  const { priceType, setPriceType } = usePaymentState();

  const transactions: TransactionType[] = useMemo(() => {
    const output = {
      full: 0,
      deposit: 0,
      amount: 0,
      currency: rooms?.[1].pricing?.payment_schedule?.[0].currency ?? "",
      transaction_type: "creditcard",
    };

    const deposit = Object.values(rooms ?? {}).reduce((sum, room) => {
      const roomDeposit = room?.pricing?.payment_schedule?.[0]?.amount ?? 0;

      return Number(sum) + Number(roomDeposit);
    }, 0);

    output.deposit = deposit;

    Object.values(rooms ?? {}).forEach((room) => {
      const paymentSchedule = room?.pricing?.payment_schedule;

      if (Array.isArray(paymentSchedule)) {
        paymentSchedule.forEach((payment) => {
          if (payment.amount) {
            output.full += parseFloat(payment.amount);
          }
        });
      }
    });

    if (priceType === "full") {
      output.amount = output.full;
    }

    if (priceType === "deposit") {
      output.amount = output.deposit;
    }

    output.amount = Math.ceil(output.amount * 100) / 100;
    output.full = Math.ceil(output.full * 100) / 100;
    output.deposit = Math.ceil(output.deposit * 100) / 100;

    return [output];
  }, [rooms, priceType]);

  const selectedTransaction = transactions[0];
  const stripePromise: Promise<Stripe | null> = loadStripe(stripe_publishable_key);
  const elementsKey = `${priceType}-${selectedTransaction?.amount}`;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.left}>
          <h1 className={styles.title}>{t("Payment")}</h1>

          <LoadingContainer isLoading={isCruiseLoading}>
            <PriceType
              full={selectedTransaction?.full ?? 0}
              deposit={selectedTransaction?.deposit ?? 0}
              currency={selectedTransaction?.currency || "USD"}
              onPriceTypeChange={setPriceType}
              priceType={priceType}
            />
          </LoadingContainer>

          <form className={styles.form}>
            {selectedTransaction && selectedTransaction.amount > 0 && (
              <Elements
                key={elementsKey}
                stripe={stripePromise}
                options={{
                  mode: "payment",
                  currency: (selectedTransaction.currency || "usd").toLowerCase(),
                  amount: Math.round(selectedTransaction.amount * 100),
                }}
              >
                <StripePaymentForm transaction={selectedTransaction} />
              </Elements>
            )}
          </form>
        </div>
        <RightSideBlock />
      </div>
    </div>
  );
}

export default Payment;
